import * as React from "react";
import { Route, Routes, createBrowserRouter, Outlet } from "react-router";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { RouterProvider } from "react-router/dom";

import logger from "../log";
import theme, { darkTheme } from "../theme";
import { ToastProvider } from "../util/useToast";

import Auth from "./Auth";
import BallCabinetUnlock from "./BallCabinetUnlock";
import DialogProvider from "./Dialog/DialogProvider";
import HelpArticle from "./HelpArticle";
import NewHomeScreen from "./Home/NewHomeScreen";
import Login from "./Login";
import Sessions from "./Sessions";
import PairDialog from "./Sessions/Pair/PairDialog";
import StackIndicator from "./StackIndicator";
import Tutorials from "./Tutorials/Tutorials";
import Loading from "./common/Loading";
import ReactLazyError from "./common/ReactLazyError";
import RequireAdmin from "./common/RequireAdmin";
import RequireAuth from "./common/RequireAuth";
import UserPage from "./common/UserPage";
import { SportProvider } from "./common/context/sport";
import { CurrentUserProvider } from "./hooks/currentUser";
import { DebugProvider } from "./hooks/debugView";
import { PairingProvider } from "./hooks/pairingStatus";
import { StatusProvider } from "./hooks/status";
import { IntercomProvider } from "./hooks/useIntercom";
import { JourneyProvider } from "./hooks/useJourney";
import { LocalizationProvider } from "./hooks/usePosition";
import { TrainerFeatureProvider } from "./hooks/useTrainerFeatures";
import { UpdateProvider } from "./hooks/useUpdate";

function handleImportError(e: Error) {
    logger.error(`Error lazy loading component: ${e.message}`, undefined, e);
    return { default: () => <ReactLazyError /> };
}

const Account = React.lazy(() => import("./Account").catch(handleImportError));
const Admin = React.lazy(() => import("./Admin").catch(handleImportError));
const Experiences = React.lazy(() =>
    import("./Experiences").catch(handleImportError),
);
const Journeys = React.lazy(() =>
    import("./Journeys/Journeys").catch(handleImportError),
);
const PlayerPTI = React.lazy(() =>
    import("./PTI/PlayerPTI").catch(handleImportError),
);
const Signup = React.lazy(() => import("./Signup").catch(handleImportError));
const Stats = React.lazy(() =>
    import("./Stats/Stats").catch(handleImportError),
);
const Trainer = React.lazy(() => import("./Trainer").catch(handleImportError));
const ProGrading = React.lazy(() =>
    import("./Journeys/ProGrading").catch(handleImportError),
);
const Performance = React.lazy(() =>
    import("./Performance").catch(handleImportError),
);
const DuprDashboard = React.lazy(() =>
    import("./DuprDashboard").catch(handleImportError),
);
const PadelDashboard = React.lazy(() =>
    import("./PadelDashboard").catch(handleImportError),
);

function ContextProviders({
    children,
}: React.PropsWithChildren): React.JSX.Element {
    return (
        <CurrentUserProvider>
            <ToastProvider>
                <PairingProvider>
                    <DebugProvider>
                        <StatusProvider>
                            <SportProvider>
                                <TrainerFeatureProvider>
                                    <IntercomProvider>
                                        <LocalizationProvider>
                                            <JourneyProvider>
                                                <UpdateProvider>
                                                    <DialogProvider>
                                                        {children}
                                                    </DialogProvider>
                                                </UpdateProvider>
                                            </JourneyProvider>
                                        </LocalizationProvider>
                                    </IntercomProvider>
                                </TrainerFeatureProvider>
                            </SportProvider>
                        </StatusProvider>
                    </DebugProvider>
                </PairingProvider>
            </ToastProvider>
        </CurrentUserProvider>
    );
}

function Root(): React.JSX.Element {
    return (
        <Routes>
            {/* Authenticated Routes */}
            <Route
                element={
                    <ContextProviders>
                        <RequireAuth>
                            <Outlet />
                            <PairDialog />
                        </RequireAuth>
                    </ContextProviders>
                }
            >
                {/* Pages with the UserPage wrapper */}
                <Route
                    element={
                        <UserPage>
                            <Outlet />
                        </UserPage>
                    }
                >
                    <Route path="*" element={<NewHomeScreen />} />
                    <Route
                        path="/content/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <Trainer />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/player-pti/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <PlayerPTI />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/account"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <Account />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/ball-cabinet-unlock"
                        element={<BallCabinetUnlock />}
                    />
                    <Route
                        path="/journeys/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <Journeys publicFacing={false} />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/performance/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <Performance />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/dupr-dashboard/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <DuprDashboard />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/padel-dashboard/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <PadelDashboard />
                            </React.Suspense>
                        }
                    />
                </Route>

                {/* Routes that implement their own window decorations */}
                <Route
                    path="/admin/*"
                    element={
                        <RequireAdmin>
                            <React.Suspense fallback={<Loading />}>
                                <Admin />
                            </React.Suspense>
                        </RequireAdmin>
                    }
                />
                {/* Admin-gated, but deliberately OUTSIDE /admin: the hardware
                    dialog workflows (DialogProvider — ball jam, faults) are
                    part of this host chrome, and that provider disables
                    itself on paths starting with /admin. Moving this under
                    /admin later also means relaxing those two path checks.
                    "/exp" rather than "/experiences" because nginx routes the
                    /experience prefix to webby-server, which would swallow
                    /experiences too. */}
                <Route
                    path="/exp/*"
                    element={
                        <RequireAdmin>
                            <React.Suspense fallback={<Loading />}>
                                <Experiences />
                            </React.Suspense>
                        </RequireAdmin>
                    }
                />
                <Route
                    path="/stats/*"
                    element={
                        <React.Suspense fallback={<Loading />}>
                            <Stats />
                        </React.Suspense>
                    }
                />
                <Route
                    path="/pro-grading/*"
                    element={
                        <ThemeProvider theme={darkTheme}>
                            <CssBaseline />
                            <React.Suspense fallback={<Loading />}>
                                <ProGrading />
                            </React.Suspense>
                        </ThemeProvider>
                    }
                />
                <Route path="/sessions/*" element={<Sessions />} />
                <Route path="/tutorials/*" element={<Tutorials />} />
                <Route path="/help/:slug" element={<HelpArticle />} />
            </Route>

            {/* Unauthenticated Routes */}
            <Route
                element={
                    <CurrentUserProvider>
                        <Outlet />
                    </CurrentUserProvider>
                }
            >
                {/* Permit unauthenticated users access to an assessment report card */}
                <Route
                    path="/external-journeys/*"
                    element={
                        <React.Suspense fallback={<Loading />}>
                            <Journeys publicFacing />
                        </React.Suspense>
                    }
                />
                <Route
                    path="/signup/*"
                    element={
                        <React.Suspense fallback={<Loading />}>
                            <Signup />
                        </React.Suspense>
                    }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/auth/*" element={<Auth />} />
            </Route>
        </Routes>
    );
}

const router = createBrowserRouter([{ path: "*", element: <Root /> }]);

export default function App(): React.JSX.Element {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <StackIndicator />
            <RouterProvider router={router} />
        </ThemeProvider>
    );
}
